import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { MatLabel } from '@angular/material/form-field';
import { ReviewService } from '../../../services/review.service';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { CommonModule } from '@angular/common';
import { Constants } from '../../../constants/constants';
import { ReviewDetails } from '../../../models/ReviewDetails';
import { Subject, takeUntil } from 'rxjs';
import { CommonDataService } from '../../../services/common-data.service';
import { ExportTitleCaseMap } from '../../../models/PdfRelatedModels';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExportObjectAsFieldValuePDFOptions } from '../../../models/ExportObjectAsFieldValuePDFOptions';
import { SiFormFieldComponent } from '../../Si-Web-Toolkit/si-form-field/si-form-field.component';
import { SiRadioButtonComponent } from '../../Si-Web-Toolkit/si-radio-button/si-radio-button.component';
import { SiCheckBoxComponent } from '../../Si-Web-Toolkit/si-check-box/si-check-box.component';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { CostReviewSummary } from '../../../models/CostReviewSummary';
import { OptionalWorkflowDirective } from '../../../Directives/optional-workflow-directive';
import { ReviewDetailTabsService } from '../../../lib/review-detail-tabs.service';
import { CostReviewProjectMgmtRequirements } from '../../../models/CostReviewProjectMgmtRequirements';
import { CostReviewDatabaseRecord } from '../../../models/CostReviewDatabaseRecord';
import { TrackChangesDirective } from '../../../Directives/track-changes.directive';

interface SolPmRequirementsForm {
  frequencyOfProjectMeetings: FormControl<string>;
  projectDuration: FormControl<string>;
  documentationRequirements: FormControl<string>;
  timeEstimatedToManageSchedule: FormControl<string>;
  estimatedPmHoursOnJobSite: FormControl<number>;
}

@Component({
  selector: 'app-sol-pm-requirements-tab',
  imports: [
    MatLabel,
    CommonModule,
    TranslatePipe,
    MatRadioModule,
    SiFormFieldComponent,
    SiRadioButtonComponent,
    SiCheckBoxComponent,
    FormsModule,
    ReactiveFormsModule,
    OptionalWorkflowDirective,
    TrackChangesDirective
  ],
  templateUrl: './sol-pm-requirements-tab.component.html',
  styleUrl: './sol-pm-requirements-tab.component.scss'
})
export class SolPMRequirementsTabComponent implements OnInit, OnDestroy {

  constants = Constants;
  costReviewSummary: CostReviewSummary | undefined = new CostReviewSummary();
  costReviewProjectMgmtRequirements: CostReviewProjectMgmtRequirements | undefined = new CostReviewProjectMgmtRequirements();
  business: string = "";
  @Input() makeHeightFull: boolean = false;
  isDisable: boolean = false;
  destroy$ = new Subject<void>();
  @Output() disablePdf = new EventEmitter<boolean>();
  pmRequirementForm!: FormGroup<SolPmRequirementsForm>;
  isReadOnlyMode: boolean = false;
  optionalWorkflowCondition: boolean = false;

  constructor(private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly reviewService: ReviewService,
    private translate: TranslateService,
    private formBuilder: NonNullableFormBuilder,
    private readonly reviewDetailsTabService: ReviewDetailTabsService) { }

  ngOnInit(): void {
    this.createForm();
    this.initializePMRequirementsData();
    this.business = this.reviewService.getBusiness();
    if (this.business && (this.business === this.constants.BUSINESS_AUTOMATION 
      || this.business === this.constants.BUSINESS_ELECTRICAL 
      || this.business === this.constants.BUSINESS_MECHANICAL
      || this.business === this.constants.BUSINESS_FIRE)) {
      this.optionalWorkflowCondition = this.reviewDetailsTabService.getOptionalWorkflowAndEstimateType()?.optionalWorkflow || false;
    }
    this.getReviewLibraryState();
    this.commonDataService.triggerTabMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.exportToPdf(this.reviewService.selectedReview, Constants.SOL_PM_REQUIREMENTS);
      });
    this.sendDisablePdfEvent();
    this.setupReadOnlyMode();
    this.updateFormEditability();
    this.subscribeToReviewFormDataSyncRequest();
  }

  get isProjectMgmtRequirementsChecked(): boolean {
    return !!this.costReviewProjectMgmtRequirements?.isProjectMgmtRequirementsApplicable;
  }

  get isSectionVisible(): boolean {
    if (this.business === Constants.BUSINESS_SECURITY) {
      return false;
    }
    return !!this.costReviewProjectMgmtRequirements || (this.isProjectMgmtRequirementsChecked && this.optionalWorkflowCondition);
  }

  get isSectionLocked(): boolean {
    return this.optionalWorkflowCondition && !this.isProjectMgmtRequirementsChecked;
  }

  onProjectMgmtRequirementsToggle(checked: boolean): void {
    if (!this.costReviewProjectMgmtRequirements) return;
    this.costReviewProjectMgmtRequirements.isProjectMgmtRequirementsApplicable = checked;
    this.updateFormEditability();
  }

  private updateFormEditability(): void {
    if (!this.pmRequirementForm) return;
    const lockedByLibrary = !this.reviewDetailsTabService.getIsLibraryUsedInSQ();
    if (lockedByLibrary || this.isSectionLocked) {
      this.pmRequirementForm.disable();
    } else {
      this.pmRequirementForm.enable();
    }
  }

  /** Allows the host app to force this tab to push its latest data without waiting for tab switch/destroy. */
  private subscribeToReviewFormDataSyncRequest(): void {
    this.reviewDetailsTabService.getReviewFormDataSyncRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pushFormDataToService());
  }

  private getReviewLibraryState(): void {
    this.isDisable = this.reviewDetailsTabService.getReviewLibraryDisableFlag();
  }



  private setupReadOnlyMode(): void {
    this.commonDataService.libraryReadOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly: boolean) => {
        const isLibraryUsedInSQ = this.reviewDetailsTabService.getIsLibraryUsedInSQ();
        if (isLibraryUsedInSQ) {
          this.isDisable = readOnly;
          this.isReadOnlyMode = false;
        } else {
          this.isReadOnlyMode = readOnly;
        }
        this.updateFormEditability();
      });
    this.subscribeToDataRefresh();
  }

  private subscribeToDataRefresh(): void {
    this.reviewService.onDataRefreshed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const isLibraryUsedInSQ = this.reviewDetailsTabService.getIsLibraryUsedInSQ();
        this.isDisable = isLibraryUsedInSQ ? this.commonDataService.libraryReadOnlyMode : this.isDisable;
        this.isReadOnlyMode = isLibraryUsedInSQ ? false : this.commonDataService.libraryReadOnlyMode;
        this.updateFormEditability();
      });
  }

  createForm() {
    this.pmRequirementForm = this.formBuilder.group<SolPmRequirementsForm>({
      frequencyOfProjectMeetings: this.formBuilder.control(Constants.EMPTY_STRING),
      projectDuration: this.formBuilder.control(Constants.EMPTY_STRING),
      documentationRequirements: this.formBuilder.control(Constants.EMPTY_STRING),
      timeEstimatedToManageSchedule: this.formBuilder.control(Constants.EMPTY_STRING),
      estimatedPmHoursOnJobSite: this.formBuilder.control(Constants.ZERO)
    });
  }

  initializePMRequirementsData() {
    const costReviewData: CostReviewDatabaseRecord = this.reviewService.costReviewDetails;
    this.costReviewProjectMgmtRequirements = costReviewData.reviewObject?.costReviewProjectMgmtRequirements;
    this.costReviewSummary = costReviewData.reviewObject?.costReviewSummary;
    this.mapPMRequirementsFormValues();
  }

  mapPMRequirementsFormValues(){
    this.pmRequirementForm.get('frequencyOfProjectMeetings')?.patchValue(this.costReviewProjectMgmtRequirements?.frequencyOfProjectMeetings || Constants.EMPTY_STRING);
    this.pmRequirementForm.get('projectDuration')?.patchValue(this.costReviewSummary?.projectDuration?.toString() || Constants.EMPTY_STRING);
    this.pmRequirementForm.get('documentationRequirements')?.patchValue(this.costReviewProjectMgmtRequirements?.documentationRequirements || Constants.EMPTY_STRING);
    this.pmRequirementForm.get('timeEstimatedToManageSchedule')?.patchValue(this.getParameterValues(Constants.YES_NO,this.costReviewProjectMgmtRequirements?.timeEstimatedToManageSchedule));
    this.pmRequirementForm.get('estimatedPmHoursOnJobSite')?.patchValue(this.costReviewProjectMgmtRequirements?.estimatedPmHoursOnJobSite || Constants.ZERO);
  }


  getParameterValues(key: string, value: string | number | undefined): string {
    return this.reviewService.getCostReviewParametersDescription(key, value);
  }

  private getParameterCode(description: string, currentValue: number): number {
    const code = this.reviewService.getCostReviewParametersCode(Constants.YES_NO, description);
    return code !== undefined ? code : currentValue;
  }

  isSectionNotApplicableShow(): boolean {
    return !this.isSectionVisible && (this.business === this.constants.BUSINESS_FIRE || this.business === this.constants.BUSINESS_ELECTRICAL || this.business === this.constants.BUSINESS_AUTOMATION || this.business === this.constants.BUSINESS_MECHANICAL) && this.makeHeightFull;
  }

  private exportPMRequirementsPdf(data: any, title: string, selectedReview: ReviewDetails) {
    const options: ExportObjectAsFieldValuePDFOptions = {
      data,
      title,
      fileName: 'pm-requirements.pdf',
      estimateID: selectedReview.estimateID,
      estimateName: selectedReview.request,
      tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.PM_REQUIREMENTS'),
    };
    this.commonFunctionsService.exportObjectAsFieldValuePDF(options);
  }

  exportToPdf(selectedReview: ReviewDetails, tabName: string) {
    if (tabName === Constants.SOL_PM_REQUIREMENTS) {
      if (
        !this.costReviewProjectMgmtRequirements ||
        !this.costReviewProjectMgmtRequirements.isProjectMgmtRequirementsApplicable
      ) {
        this.exportPMRequirementsPdf(
          { [this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.PM_REQUIREMENTS_DATA_NOT_FOUND')]: Constants.EMPTY_STRING },
          Constants.EMPTY_STRING,
          selectedReview
        );
        return;
      }

      const fields = [
        { key: this.translate.instant('REVIEW_SOL_MARGINS_TAB.PROJECT_DURATION'), label: this.translate.instant('REVIEW_SOL_MARGINS_TAB.PROJECT_DURATION'), value: this.costReviewSummary?.projectDuration ? `${this.costReviewSummary.projectDuration} Days` : Constants.EMPTY_STRING },
        {
          key: this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.FREQUENCY_OF_PROJECT_MEETINGS'),
          label: (this.business === this.constants.BUSINESS_AUTOMATION || this.business === this.constants.BUSINESS_ELECTRICAL || this.business === this.constants.BUSINESS_MECHANICAL)
            ? this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.FREQUENCY_OF_PROJECT_MEETINGS')
            : this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.PROJECT_MEETINGS_FREQUENCY'),
          value: this.costReviewProjectMgmtRequirements?.frequencyOfProjectMeetings
        },
        { key: this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.DOCUMENTATION_REQUIREMENTS'), label: this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.DOCUMENTATION_REQUIREMENTS'), value: this.costReviewProjectMgmtRequirements?.documentationRequirements },
        {
          key: this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.TIME_ESTIMATED_TO_MANAGE_SCHEDULE'),
          label: this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.TIME_ESTIMATED_TO_MANAGE_SCHEDULE'),
          value: this.getParameterValues(this.constants.YES_NO, this.costReviewProjectMgmtRequirements?.timeEstimatedToManageSchedule)
        },
        { key: this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.ESTIMATED_PM_HOURS'), label: this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.ESTIMATED_PM_HOURS'), value: this.costReviewProjectMgmtRequirements?.estimatedPmHoursOnJobSite }
      ];

      const exportData: ExportTitleCaseMap = {};
      fields.forEach(f => exportData[f.key] = f.value !== undefined && f.value !== null ? String(f.value) : undefined);

      this.exportPMRequirementsPdf(
        exportData,
        this.translate.instant('REVIEW_SOL_PM_REQUIREMENTS_TAB.TITLE'),
        selectedReview
      );
    }
  }

  sendDisablePdfEvent() {
    this.disablePdf.emit(false);
  }

  ngOnDestroy() {
    this.pushFormDataToService();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private pushFormDataToService(): void {
    if (!this.pmRequirementForm || !this.costReviewProjectMgmtRequirements) return;
    const formData = this.pmRequirementForm.getRawValue();

    // Direct string fields
    this.costReviewProjectMgmtRequirements.frequencyOfProjectMeetings = formData.frequencyOfProjectMeetings;
    this.costReviewProjectMgmtRequirements.documentationRequirements = formData.documentationRequirements;

    // Direct number field
    this.costReviewProjectMgmtRequirements.estimatedPmHoursOnJobSite = this.commonFunctionsService.toNumberOrCurrent(formData.estimatedPmHoursOnJobSite, this.costReviewProjectMgmtRequirements.estimatedPmHoursOnJobSite);

    // Parameter lookup field
    this.costReviewProjectMgmtRequirements.timeEstimatedToManageSchedule = this.getParameterCode(formData.timeEstimatedToManageSchedule, this.costReviewProjectMgmtRequirements.timeEstimatedToManageSchedule);
  }

  resetIfEmpty(): void {
    const control = this.pmRequirementForm.get('frequencyOfProjectMeetings');
    if (control?.value === Constants.EMPTY_STRING) {
      control.setValue(Constants.ZERO.toString());
    }
  }

  resetEstimatedPmHoursIfEmpty(): void {
    const control = this.pmRequirementForm.controls.estimatedPmHoursOnJobSite;
    if (String(control.value) === Constants.EMPTY_STRING) {
      control.setValue(Constants.ZERO);
    }
  }

}
